import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Incident Response and Reporting Policy
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography>Incident Response and Reporting Policy</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            This section of the documentation provides an overview of our Incident Response and Reporting procedures
            designed to comply with the Health Insurance Portability and Accountability Act (HIPAA). It is crucial for
            our SaaS medical application to have an effective incident response plan to manage and mitigate any
            potential security incidents involving Protected Health Information (PHI). This ensures the confidentiality,
            integrity, and availability of PHI are maintained, and supports our dedication to protecting our users' and
            patients' data. Here, we describe our preparedness, response, and recovery strategies, including how
            incidents are reported, assessed, and resolved, as well as how we communicate these incidents to affected
            parties and relevant authorities.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Incident Response and Reporting Overview
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Incident Response Plan:</b> Details the steps to be taken immediately after an incident is
                  detected. This includes identifying the type of incident, containing the breach, eradicating the
                  threat, recovering systems, and conducting a post-incident analysis to prevent future breaches.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Reporting to Authorities:</b> Outlines the procedures for reporting breaches to necessary
                  authorities, including the Department of Health and Human Services (HHS), and in certain cases, to the
                  media and affected individuals. This section includes timelines, thresholds for reporting, and the
                  types of information to be disclosed.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Documentation and Review:</b> Describes the documentation of the incident and the subsequent review
                  process. This ensures lessons are learned and that the incident response plan is updated to reflect
                  new insights and strengthen security measures against future incidents.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Objectives
          </Typography>
          <List>
            <ListItem>
              To promptly identify, contain, and mitigate any unauthorized access or breaches involving PHI.
            </ListItem>
            <ListItem>
              To ensure compliance with legal and regulatory reporting requirements, minimizing potential harm to
              affected individuals and preserving the integrity of our systems.
            </ListItem>
            <ListItem>
              To review and refine our incident response strategy regularly, enhancing our resilience against future
              security incidents.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
